import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearAuth, restoreAuthFromStorage } from "../api/Client";
import { scheduleManyExpiryLocal, cancelExpiryLocal } from "../notifications/scheduleExpiryLocal";
import { Capacitor } from '@capacitor/core';
import {API_BASE} from "../config.ts";

interface Service {
  id: number;
  name: string;
  image?: string | null;
}

interface UserProduct {
  id: number;
  active_product_id?: number | null;
  name: string;
  price?: number | null;
  image?: string | null;
  serviceIds?: number[];
  is_active?: boolean;
  status_detail?: string;
  usage_text?: string | null;
  type_of_product?: string;
  total_session?: number | null;
  sessions_remaining?: number | null;
  sessions_consumed?: number | null;
  weekly_limit?: number | null;
  expiry_date?: string | null;
  subscription_status?: string | null;
}

export default function ProductPage() {
  const [activeTab, setActiveTab] = useState<"my" | "hire">("my");
  const [services, setServices] = useState<Service[]>([]);
  const [userProducts, setUserProducts] = useState<UserProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [showUnassignForm, setShowUnassignForm] = useState(false);
  const [motivoSeleccionado, setMotivoSeleccionado] = useState("");
  const [comentarioLibre, setComentarioLibre] = useState("");


  const navigate = useNavigate();

  // Estado para modal de acciones
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<UserProduct | null>(null);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const { user } = await restoreAuthFromStorage({ refreshSession: true });
        if (!user?.id) {
          await clearAuth();
          navigate("/home", { replace: true });
          return;
        }

        // Llamadas en paralelo
        const [servicesRes, productsRes] = await Promise.all([
          api.get<Service[]>("/api/customers/catalog/services"),
          api.get<UserProduct[]>("/api/customers/catalog/user-products"),
        ]);

        const servicesData = Array.isArray(servicesRes.data) ? servicesRes.data : [];
        const productsData = Array.isArray(productsRes.data) ? productsRes.data : [];

        setServices(servicesData);

        // Normalizar productos y calcular estado
        const products = productsData.map((p) => {
          let is_active = true;
          let status_detail = "";
          let usage_text: string | null = null;

          if (p.type_of_product === "single_session" || p.type_of_product === "multi_sessions") {
            const consumed = p.sessions_consumed ?? 0;
            const total = p.total_session ?? 0;
            usage_text = `${consumed}/${total}`;
            if ((p.sessions_remaining ?? 0) <= 0) {
              status_detail = "Completado";
              is_active = false;
            }
          } else if (p.type_of_product === "recurrent") {
            const consumed = p.sessions_consumed ?? 0;
            const limit = p.weekly_limit ?? 0;
            usage_text = `${consumed}/${limit}`;
            if (limit > 0 && consumed >= limit) {
              status_detail = "Completado";
              is_active = false;
            }
          }

          return { ...p, is_active, status_detail, usage_text };
        });

        setUserProducts(products);

        const isWeb = Capacitor.getPlatform() === 'web';

        const isEligible = (t?: string | null) => (t ?? "").toLowerCase() !== "recurrent";

        const schedulables = products
          .filter(p => p?.expiry_date && isEligible(p.type_of_product))
          .map(p => ({
            productId: Number(p.id),
            productName: String(p.name ?? ""),
            // Si tu planificador espera 'YYYY-MM-DD', recorta por si viniera con hora:
            expiryISO: String(p.expiry_date).slice(0, 10),
          }));

        if (!isWeb && schedulables.length) {
          scheduleManyExpiryLocal(schedulables).catch(e =>
            console.warn("Local notify error", e)
          );
        }
      } catch (err: any) {
        setError(err?.response?.data?.error || err.message || "Error al cargar datos");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  async function handleUnassign(productId: number, motivo: string = "", comentario: string = "", activeProductId: number) {
    try {
      const { user } = await restoreAuthFromStorage();
      if (!user?.id) throw new Error("Usuario no disponible");
      await api.delete("/api/mobile/unassign-product", {
        params: { user_id: user.id, product_id: productId, active_product_id: activeProductId },
        data: { reason: motivo, comment: comentario }
      } as any);
      await cancelExpiryLocal(productId);
      setUserProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch (err: any) {
      alert(err?.response?.data?.error || err.message || "Error al darse de baja");
    }
  }

  function handleServiceClick(serviceId: number) {
    navigate(`/service-products/${serviceId}`);
  }

  function handleViewDetail(activeProductId: number | undefined, productId: number) {
    navigate(`/product/${productId}`, { state: { activeProductId } });
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", overflowY: "auto", paddingBottom: "96px" }}>
      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #ddd" }}>
        <button
          onClick={() => setActiveTab("my")}
          style={{
            flex: 1,
            padding: "0.75rem",
            border: "none",
            borderBottom: activeTab === "my" ? "2px solid #1976d2" : "2px solid transparent",
            background: "none",
            color: activeTab === "my" ? "#1976d2" : "#555",
            fontWeight: activeTab === "my" ? 600 : 400,
            cursor: "pointer",
          }}
          data-tour="products-tab-my"
        >
          Mis productos
        </button>
        <button
          onClick={() => setActiveTab("hire")}
          style={{
            flex: 1,
            padding: "0.75rem",
            border: "none",
            borderBottom: activeTab === "hire" ? "2px solid #1976d2" : "2px solid transparent",
            background: "none",
            color: activeTab === "hire" ? "#1976d2" : "#555",
            fontWeight: activeTab === "hire" ? 600 : 400,
            cursor: "pointer",
          }}
          data-tour="products-tab-hire"
        >
          Contratar
        </button>
      </div>

      {loading && <p style={{ padding: "1rem" }}>Cargando…</p>}
      {error && <p style={{ padding: "1rem", color: "red" }}>{error}</p>}

      {/* Mis productos */}
      {!loading && activeTab === "my" && (
        <div style={{ flex: 1, padding: "0.75rem" }}>
          {userProducts.length === 0 ? (
            <p>No tienes productos contratados.</p>
          ) : (
            userProducts.map((prod) => (
              <div
                key={prod.id}
                onClick={() => {
                  setSelectedProduct(prod);
                  setShowActionModal(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.9rem",
                  marginBottom: "0.75rem",
                  border: "1px solid #f0f0f0",
                  borderRadius: "12px",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  cursor: "pointer",
                }}
              >
                {prod.image && prod.image !== "default.png" && (
                  <img
                    src={`${API_BASE}/api/product_images/${prod.image}`}
                    alt={String(prod.name)}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    style={{ width: "64px", height: "64px", objectFit: "contain", marginRight: "16px" }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                    {String(prod.name).toUpperCase()}
                  </div>
                  
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Modal acciones */}
      {showActionModal && selectedProduct && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "20px",
            padding: "1.5rem",
            width: "80%",
            maxWidth: "400px",
            textAlign: "center",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
            Producto: {String(selectedProduct.name).toUpperCase()}
          </h3>
          <p style={{ marginBottom: "1.5rem", color: "#555" }}>
            ¿Qué deseas hacer con este producto?
          </p>

          {/* 🔹 Acciones principales */}
          {!showUnassignForm ? (
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedProduct(null);
                }}
                style={{
                  flex: 1,
                  marginRight: "0.5rem",
                  padding: "0.75rem",
                  border: "none",
                  background: "transparent",
                  color: "#1976d2",
                }}
              >
                Atrás
              </button>
              <button
                onClick={() => {
                  setShowActionModal(false);
                  const id = selectedProduct.id;
                  const activeId = selectedProduct.active_product_id === null ? undefined : selectedProduct.active_product_id;
                  setSelectedProduct(null);
                  handleViewDetail(activeId, id);
                }}
                style={{
                  flex: 1,
                  marginRight: "0.5rem",
                  padding: "0.75rem",
                  border: "none",
                  background: "transparent",
                  color: "#1976d2",
                }}
              >
                Ver detalles
              </button>

              {/* 🔹 Solo recurrentes pueden darse de baja */}
              {selectedProduct.type_of_product?.toLowerCase() === "recurrent" &&
                selectedProduct.subscription_status !== "canceled" && (
                    <button
                      onClick={() => setShowUnassignForm(true)}
                      style={{
                        flex: 1,
                        padding: "0.75rem",
                        border: "none",
                        background: "transparent",
                        color: "#c62828",
                      }}
                    >
                      Darse de baja
                    </button>
                )}

                {/* 🔹 Si la suscripción ya está cancelada pero el producto sigue en booked */}
                {selectedProduct.type_of_product?.toLowerCase() === "recurrent" &&
                selectedProduct.subscription_status === "canceled" &&
                selectedProduct.is_active && (
                    <div style={{
                      marginTop: "1rem",
                      padding: "0.75rem",
                      borderRadius: "8px",
                      background: "#fff3cd",
                      color: "#856404",
                      fontSize: "0.9rem",
                      textAlign: "center"
                    }}>
                      Ya has solicitado la baja. Tu producto seguirá activo hasta final de mes.
                    </div>
                )}
              </div>
            ) : (
              // 🔹 Formulario de baja
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (window.confirm("¿Confirmas la baja de este producto?")) {
                    const motivo = motivoSeleccionado;
                    const comentario = comentarioLibre;
                    console.log("Motivo:", motivo, "Comentario:", comentario);
                    // Aquí puedes enviar estos datos al backend
                    handleUnassign(selectedProduct.id, motivo, comentario, selectedProduct.active_product_id || 0);
                    setShowUnassignForm(false);
                    setSelectedProduct(null);
                    setShowActionModal(false);
                  }
                }}
              >
                <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: 500 }}>
                  Selecciona un motivo:
                </label>
                <select
                  required
                  value={motivoSeleccionado}
                  onChange={(e) => setMotivoSeleccionado(e.target.value)}
                  style={{ width: "100%", padding: "0.5rem", marginBottom: "1rem" }}
                >
                  <option value="">-- Selecciona --</option>
                  <option value="no_interes">Ya no me interesa</option>
                  <option value="calidad">La calidad no es la esperada</option>
                  <option value="cambio">Voy a cambiar de producto</option>
                  <option value="precio">El precio es alto</option>
                  <option value="tiempo">No tengo tiempo</option>
                  <option value="otro">Otro motivo</option>
                </select>
                <textarea
                  placeholder={motivoSeleccionado === "otro" ? "Comentario adicional (requerido)" : "Comentario adicional (opcional)"}
                  value={comentarioLibre}
                  onChange={(e) => setComentarioLibre(e.target.value)}
                  required={motivoSeleccionado === "otro"}
                  aria-required={motivoSeleccionado === "otro"}
                  style={{ width: "100%", padding: "0.5rem", minHeight: "80px", marginBottom: "1rem" }}
                />

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <button
                    type="button"
                    onClick={() => setShowUnassignForm(false)}
                    style={{
                      flex: 1,
                      marginRight: "0.5rem",
                      padding: "0.75rem",
                      border: "none",
                      background: "transparent",
                      color: "#1976d2",
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      border: "none",
                      background: "#c62828",
                      color: "#fff",
                      borderRadius: "8px",
                    }}
                  >
                    Confirmar baja
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}



      {/* Contratar */}
      {!loading && activeTab === "hire" && (
        <div style={{ flex: 1, padding: "0.75rem" }}>
          {[...services]
            .sort((a, b) => {
              if (a.id === 2) return -1;
              if (b.id === 2) return 1;
              if (a.id === 3) return -1;
              if (b.id === 3) return 1;
              return 0;
            })
            .map((svc) => (
              <div
                key={svc.id}
                onClick={() => handleServiceClick(svc.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "0.9rem",
                  marginBottom: "0.75rem",
                  border: "1px solid #f0f0f0",
                  borderRadius: "12px",
                  background: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                {svc.image && svc.image !== "default.png" && (
                  <img
                    src={`${API_BASE}/api/service_images/${svc.image}`}
                    alt={String(svc.name)}
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                    style={{ width: "64px", height: "64px", objectFit: "contain", marginRight: "16px" }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "1rem" }}>
                    {String(svc.name).toUpperCase()}
                  </div>
                </div>
                <span style={{ fontSize: "1.5rem", color: "#1976d2" }}>➔</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
